// src/services/transaction/TransactionService.ts
import { BaseTenantCrudService } from '@/services/_base/BaseTenantCrudService'
import { type TransactionRecord } from '@/types/Transaction.ts'
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class TransactionService extends BaseTenantCrudService<TransactionRecord> {
  constructor(firestore: IFirestoreService, session: Session) {
    super('transaction_records', firestore, session)
  }

  protected override beforeCreate(data: Partial<TransactionRecord>): Partial<TransactionRecord> {
    return {
      ...super.beforeCreate(data),
      partyName_lower: (data.partyName ?? '').toLowerCase(),
      is_deleted: false,
    }
  }

  protected override beforeUpdate(
    id: string,
    data: Partial<TransactionRecord>,
  ): Partial<TransactionRecord> {
    const normalized = { ...super.beforeUpdate(id, data) }

    if (data.partyName) {
      normalized.partyName_lower = data.partyName.toLowerCase()
    }

    return normalized
  }

  protected override async afterCreate(id: string, transaction: Partial<TransactionRecord>) {
    // e.g., index to CRM timeline or trigger notifications
    console.log(`[TransactionService] Created transaction ${id}`, transaction)
  }

  /** Soft delete a transaction by setting is_deleted flag */
  async softDelete(id: string): Promise<void> {
    await this.update(id, { is_deleted: true } as Partial<TransactionRecord>)
  }

  /** Get transaction summary for display */
  getTransactionSummary(transaction: TransactionRecord): {
    totalItems: number
    totalWeight: number
    totalAmount: string
  } {
    const totalItems = transaction.items?.length || 0
    const totalWeight =
      transaction.items?.reduce((sum, item) => {
        const netWeight = item.weight?.net || 0
        return sum + netWeight
      }, 0) || 0
    const totalAmount = transaction.total_amount?.toFixed(2) || '0.00'

    return {
      totalItems,
      totalWeight,
      totalAmount,
    }
  }

  /** Validate transaction for submission */
  validateTransactionForSubmission(transaction: TransactionRecord): {
    isValid: boolean
    errors: string[]
  } {
    const errors: string[] = []

    if (!transaction.party_id) {
      errors.push('Party is required')
    }

    if (!transaction.transaction_type) {
      errors.push('Transaction type is required')
    }

    if (!transaction.items || transaction.items.length === 0) {
      errors.push('At least one item is required')
    }

    if (transaction.total_amount <= 0) {
      errors.push('Total amount must be greater than 0')
    }

    if (!transaction.payment_method) {
      errors.push('Payment method is required')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /** Submit a transaction with validation */
  async submitTransaction(id: string): Promise<TransactionRecord> {
    const transaction = await this.firestore.getDoc<TransactionRecord>(this.getCollectionPath(), id)
    if (!transaction) {
      throw new Error('Transaction not found')
    }

    const validation = this.validateTransactionForSubmission(transaction)
    if (!validation.isValid) {
      throw new Error(`Transaction validation failed: ${validation.errors.join(', ')}`)
    }

    // Update transaction status to submitted
    await this.update(id, {
      transaction_status: 'submitted',
      updated_at: new Date(),
    })

    return (await this.firestore.getDoc<TransactionRecord>(
      this.getCollectionPath(),
      id,
    )) as TransactionRecord
  }

  /** Complete a transaction */
  async completeTransaction(id: string): Promise<TransactionRecord> {
    await this.update(id, {
      transaction_status: 'completed',
      payment_status: 'completed',
      updated_at: new Date(),
    })

    return (await this.firestore.getDoc<TransactionRecord>(
      this.getCollectionPath(),
      id,
    )) as TransactionRecord
  }

  /** Cancel a transaction */
  async cancelTransaction(id: string): Promise<TransactionRecord> {
    await this.update(id, {
      transaction_status: 'cancelled',
      updated_at: new Date(),
    })

    return (await this.firestore.getDoc<TransactionRecord>(
      this.getCollectionPath(),
      id,
    )) as TransactionRecord
  }
}
